import { useEffect, useRef, type FormEvent, type Ref } from 'react';
import { useTranslation, type Language } from '../contexts/TranslationContext';
import { useGuestAnswerClaim } from '../hooks/useGuestAnswerClaim';
import type { GuestAnswerReceipt } from '../services/publicGuestQuestion';
import { FadeSwitch } from './FadeSwitch';
import { ArrowRight, Loader2, Mail } from './Icons';
import { BrandButton } from './ui/BrandButton';
import { ErrorNotice } from './ui/ErrorNotice';
import './GuestAnswerEmailForm.css';

export function GuestAnswerEmailForm({ receipt, locale }: {
  receipt: GuestAnswerReceipt;
  locale: Language;
}) {
  const claim = useGuestAnswerClaim(receipt, locale);
  return <GuestAnswerEmailFormView claim={{ ...claim, captchaContainerRef: claim.bot.captchaContainerRef }} />;
}

export type GuestAnswerEmailFormState = Pick<ReturnType<typeof useGuestAnswerClaim>,
  'email' | 'setEmail' | 'website' | 'setWebsite' | 'sentTo' | 'error' | 'submit' | 'isPending'> & {
  captchaContainerRef: Ref<HTMLDivElement>;
};

export function GuestAnswerEmailFormView({ claim }: { claim: GuestAnswerEmailFormState }) {
  const { t } = useTranslation();
  const confirmationRef = useRef<HTMLElement>(null);
  const key = 'presentation.guestQuestion.gate';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void claim.submit();
  };

  useEffect(() => {
    if (!claim.sentTo) return;
    const frame = window.requestAnimationFrame(() => {
      confirmationRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [claim.sentTo]);

  return (
    <div data-guest-answer-email className="mx-auto w-full min-w-0 text-left">
      <div
        aria-hidden={claim.sentTo ? true : undefined}
        {...(claim.sentTo ? { inert: '' } : {})}
        className={`grid transition-[grid-template-rows,opacity] duration-300 motion-reduce:transition-none ${claim.sentTo ? 'pointer-events-none grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`}
      >
        <div className="min-h-0 overflow-hidden">
          <h3 id="landing-guest-answer-title" className="mb-5 text-center text-sm font-extrabold leading-6 text-[rgb(var(--theme-text))] sm:mb-6 sm:text-lg sm:leading-7">{t(`${key}.previewDescription`)}</h3>
          <form onSubmit={submit} className="relative mx-auto w-full max-w-md" aria-busy={claim.isPending}>
            <div className="pointer-events-none absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="guest-answer-website">Website</label>
              <input id="guest-answer-website" name="website" value={claim.website} onChange={(event) => claim.setWebsite(event.target.value)} disabled={claim.isPending} autoComplete="off" tabIndex={-1} />
            </div>
            <label htmlFor="guest-answer-email" className="sr-only">{t(`${key}.emailLabel`)}</label>
            <div className="relative">
              <Mail aria-hidden="true" className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 sm:left-4 sm:top-4 sm:h-5 sm:w-5 text-[rgb(var(--theme-muted))]" />
              <input
                id="guest-answer-email"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                required
                maxLength={254}
                value={claim.email}
                onChange={(event) => claim.setEmail(event.target.value)}
                disabled={claim.isPending}
                placeholder={t(`${key}.emailPlaceholder`)}
                aria-invalid={claim.error === 'disposable' || undefined}
                className="guest-answer-email-input min-h-11 w-full rounded-xl border border-[rgb(var(--theme-border-strong))] bg-[rgb(var(--theme-surface))] py-3 pl-10 pr-3 text-sm disabled:opacity-60 sm:min-h-14 sm:pl-12 sm:pr-4 sm:text-base"
              />
            </div>
            <div className="mx-auto mt-4 w-fit max-w-full" ref={claim.captchaContainerRef} />
            <FadeSwitch viewKey={claim.error ? claim.error : 'none'}>
              {claim.error && <ErrorNotice className="mt-4">{t(`${key}.errors.${claim.error}`)}</ErrorNotice>}
            </FadeSwitch>
            <BrandButton type="submit" disabled={!claim.email.trim() || claim.isPending} className="guest-answer-email-submit mt-4 min-h-11 w-full text-xs sm:mt-5 sm:min-h-14 sm:text-base">
              <FadeSwitch viewKey={claim.isPending ? 'sending' : 'ready'}>
                <span className="inline-flex items-center gap-2">
                  {claim.isPending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : <ArrowRight className="h-4 w-4" />}
                  {t(`${key}.${claim.isPending ? 'sending' : 'submit'}`)}
                </span>
              </FadeSwitch>
            </BrandButton>
          </form>
          <p className="mx-auto mt-3 max-w-md text-center text-[0.6875rem] leading-[1.6] text-[rgb(var(--theme-muted))] sm:mt-4 sm:text-xs">
            {t(`${key}.termsPrefix`)} <a href="/terms" className="brand-focus-ring underline">{t(`${key}.terms`)}</a> {t(`${key}.and`)} <a href="/privacy" className="brand-focus-ring underline">{t(`${key}.privacy`)}</a>
          </p>
        </div>
      </div>
      <FadeSwitch viewKey={claim.sentTo ? 'sent' : 'empty'}>
        {claim.sentTo && (
          <section ref={confirmationRef} role="status" aria-live="polite" aria-atomic="true" aria-labelledby="guest-answer-email-title" tabIndex={-1} className="guest-answer-confirmation mx-auto w-full max-w-md rounded-sm text-center">
            <div aria-hidden="true" className="guest-answer-confirmation-mark theme-rating-saved">
              <svg viewBox="0 0 64 64" fill="none" className="h-14 w-14 sm:h-16 sm:w-16">
                <path className="guest-answer-confirmation-ring" d="M27 7Q32 2 37 7L57 27Q62 32 57 37L37 57Q32 62 27 57L7 37Q2 32 7 27Z" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" pathLength="1" />
                <path className="guest-answer-confirmation-tick" d="m21.5 32.5 7.5 7.5 15-15.5" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" pathLength="1" />
              </svg>
            </div>
            <h3 id="guest-answer-email-title" className="sr-only">{t(`${key}.sentTitle`)}</h3>
            <p className="mt-3 text-xs leading-5 text-[rgb(var(--theme-muted))] sm:text-sm sm:leading-6">{t(`${key}.sentDescription`)}</p>
            <p className="mt-2 break-all text-sm font-bold sm:text-base">{claim.sentTo}</p>
            <ol className="mx-auto mt-5 max-w-sm space-y-2.5 text-left text-xs leading-5 text-[rgb(var(--theme-muted))] sm:mt-6 sm:space-y-3 sm:text-sm sm:leading-6">
              {['openEmail', 'followLink', 'readAnswer'].map((step, index) => (
                <li key={step} className="flex gap-2.5 sm:gap-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--theme-primary)/0.1)] text-[0.6875rem] font-extrabold text-[rgb(var(--theme-primary-strong))] sm:h-6 sm:w-6 sm:text-xs">{index + 1}</span>
                  <span className="font-bold">{t(`${key}.${step}`)}</span>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-[0.6875rem] leading-[1.6] text-[rgb(var(--theme-muted))] sm:mt-5 sm:text-xs">{t(`${key}.spamHint`)}</p>
          </section>
        )}
      </FadeSwitch>
    </div>
  );
}
